import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Zap } from "lucide-react";
import CrudPage from "@/components/CrudPage";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Origins — read-only master data auto-imported from ProRoast.
 *
 * Adds a preflight banner + one-click "Activate all" action when any
 * origins have `is_active=false`. Reason: ProRoast doesn't map its
 * "active" flag consistently, so the tablet's `?active=true` picker
 * can end up empty even when there's plenty of stock. Owner-only fix.
 */
export default function Origins() {
  const { user } = useAuth();
  const isOwner = user?.role === "Owner";
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activating, setActivating] = useState(false);
  const [flash, setFlash] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);   // remounts CrudPage after a bulk change

  const checkInactive = useCallback(async () => {
    try {
      const { data } = await api.get("/origins");
      setTotalCount(data.length);
      setInactiveCount(data.filter((o) => o.is_active === false).length);
    } catch { /* silent — CrudPage will surface its own error */ }
  }, []);

  useEffect(() => { checkInactive(); }, [checkInactive, reloadKey]);

  const activateAll = async () => {
    if (!window.confirm(
      `Activate all ${totalCount} origins? The tablet's origin picker `
      + `will immediately show every origin. Owner can still mark `
      + `individual origins inactive afterwards.`
    )) return;
    setActivating(true);
    setFlash(null);
    try {
      const { data } = await api.post("/myroast/admin/origins/activate-all");
      setFlash({
        kind: "ok",
        text: `${data.modified ?? data.matched ?? "All"} origins activated. The tablet picker will populate on next sync.`,
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      setFlash({ kind: "err", text: formatApiError(err.response?.data?.detail) || "Failed to activate origins" });
    } finally {
      setActivating(false);
    }
  };

  return (
    <div data-testid="origins-page-wrapper">
      {isOwner && inactiveCount > 0 && (
        <div
          className="mb-4 flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3"
          data-testid="origins-inactive-warning"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-orange-600" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-orange-900">
              {inactiveCount} of {totalCount} origins are inactive
            </p>
            <p className="text-sm text-orange-900/80 mt-0.5">
              The MyRoast tablet&apos;s origin picker filters by <code className="text-xs px-1 py-0.5 rounded bg-orange-100">active=true</code>.
              Any inactive origins won&apos;t appear, so roasters can&apos;t select them
              when starting a batch. Activate them below.
            </p>
          </div>
          <button
            onClick={activateAll}
            disabled={activating}
            data-testid="origins-activate-all-btn"
            className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 bg-orange-600 text-white rounded text-sm font-semibold hover:bg-orange-700 disabled:opacity-60"
          >
            <Zap className="w-4 h-4" />
            {activating ? "Activating…" : "Activate all"}
          </button>
        </div>
      )}
      {flash && (
        <div
          className={`mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
            flash.kind === "ok"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-destructive/30 bg-destructive/5 text-destructive"
          }`}
          data-testid="origins-activate-flash"
        >
          {flash.kind === "ok"
            ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{flash.text}</span>
        </div>
      )}

      <CrudPage
        key={reloadKey}
        eyebrow="Master data"
        title="Origins"
        description="Coffee origins synced from ProRoast. This list is read-only — origins are auto-validated and added on every ProRoast sync."
        testIdPrefix="origins"
        resource="origins"
        readOnly
        readOnlyNotice="Origins are imported and validated automatically from your ProRoast sync. To add a new origin, create it in ProRoast and trigger a Drive sync."
        columns={[
          { key: "name", label: "Origin" },
          { key: "country", label: "Country" },
          { key: "varietal", label: "Varietal" },
          { key: "processing_method", label: "Process" },
          { key: "altitude", label: "Altitude (m)", align: "right" },
          { key: "is_active", label: "Active", render: (o) => o.is_active ? "Yes" : "No" },
        ]}
        fields={[]}
        emptyHint="Origins will appear here after the first ProRoast sync."
      />
    </div>
  );
}
